import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Upload, FileText, Book, Database, Trash2, Download } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";

function DataLake({ user, onLogout }) {
  const navigate = useNavigate();
  const [loadingContratos, setLoadingContratos] = useState(false);
  const [loadingLegislacion, setLoadingLegislacion] = useState(false);
  const [documentosContratos, setDocumentosContratos] = useState([]);
  const [documentosLegislacion, setDocumentosLegislacion] = useState([]);
  const [loadingListaContratos, setLoadingListaContratos] = useState(false);
  const [loadingListaLegislacion, setLoadingListaLegislacion] = useState(false);
  
  // Estados para modal de subida
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadTipo, setUploadTipo] = useState(""); // "contratos" o "legislacion"
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadDescripcion, setUploadDescripcion] = useState("");

  // Verificar acceso y permisos
  const isCEO = user.email === "mpino@allcom.cl";
  const tieneAcceso = isCEO || user.acceso_data_lake;
  const puedeSubir = isCEO || user.puede_subir_data_lake;
  const puedeEliminar = isCEO || user.puede_eliminar_data_lake;
  
  if (!tieneAcceso) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Database className="h-16 w-16 text-tesla-grayMedium mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-tesla-black mb-2">Acceso Restringido</h2>
          <p className="text-tesla-grayMedium mb-6">No tienes permisos para acceder al Data Lake</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-tesla-red hover:bg-red-700 text-white">
            Volver al Dashboard
          </Button>
        </div>
      </div>
    );
  }

  useEffect(() => {
    loadDocumentosContratos();
    loadDocumentosLegislacion();
  }, []);

  const loadDocumentosContratos = async () => {
    setLoadingListaContratos(true);
    try {
      const response = await api.get('/data-lake/contratos');
      setDocumentosContratos(response.data);
    } catch (error) {
      console.error('Error cargando documentos de contratos:', error);
    } finally {
      setLoadingListaContratos(false);
    }
  };

  const loadDocumentosLegislacion = async () => {
    setLoadingListaLegislacion(true);
    try {
      const response = await api.get('/data-lake/legislacion');
      setDocumentosLegislacion(response.data);
    } catch (error) {
      console.error('Error cargando documentos de legislación:', error);
    } finally {
      setLoadingListaLegislacion(false);
    }
  };

  const handleDownloadDataLake = async (tipo, doc) => {
    try {
      const res = await api.get(`/data-lake/${tipo}/${doc.id}/descargar`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Documento descargado");
    } catch (error) {
      let detail = "";
      const data = error.response?.data;
      if (data instanceof Blob) { try { detail = JSON.parse(await data.text()).detail || ""; } catch {} }
      if (detail.startsWith("ARCHIVO_PERDIDO")) toast.error(`El archivo "${doc.filename}" ya no existe en el servidor. Debes resubirlo.`, { duration: 8000 });
      else toast.error("Error descargando documento");
    }
  };

  const handleDeleteContrato = async (docId, filename) => {
    if (!window.confirm(`¿Eliminar "${filename}"?`)) return;
    
    try {
      await api.delete(`/data-lake/contratos/${docId}`);
      toast.success('Documento eliminado');
      await loadDocumentosContratos();
    } catch (error) {
      console.error('Error eliminando documento:', error);
      toast.error('Error al eliminar documento');
    }
  };

  const handleDeleteLegislacion = async (docId, filename) => {
    if (!window.confirm(`¿Eliminar "${filename}"?`)) return;
    
    try {
      await api.delete(`/data-lake/legislacion/${docId}`);
      toast.success('Documento eliminado');
      await loadDocumentosLegislacion();
    } catch (error) {
      console.error('Error eliminando documento:', error);
      toast.error('Error al eliminar documento');
    }
  };

  const handleOpenUploadModal = (tipo, files) => {
    if (files.length === 0) return;
    setUploadTipo(tipo);
    setUploadFiles(Array.from(files));
    setUploadDescripcion("");
    setShowUploadModal(true);
  };

  const handleConfirmUpload = async () => {
    if (!uploadDescripcion.trim()) {
      toast.error('La descripción es obligatoria');
      return;
    }

    const tipo = uploadTipo === "contratos" ? "contrato_prueba" : "legislacion";
    const setLoading = uploadTipo === "contratos" ? setLoadingContratos : setLoadingLegislacion;
    const loadDocs = uploadTipo === "contratos" ? loadDocumentosContratos : loadDocumentosLegislacion;
    const endpoint = uploadTipo === "contratos" ? '/data-lake/contratos' : '/data-lake/legislacion';

    setLoading(true);
    try {
      for (const file of uploadFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('tipo', tipo);
        formData.append('descripcion', uploadDescripcion.trim());

        await api.post(endpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(`${uploadFiles.length} documento(s) subido(s) correctamente`);
      setShowUploadModal(false);
      setUploadFiles([]);
      setUploadDescripcion("");
      await loadDocs();
    } catch (error) {
      console.error('Error subiendo documentos:', error);
      toast.error(error.response?.data?.detail || 'Error al subir documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadContratos = async (e) => {
    const files = Array.from(e.target.files);
    handleOpenUploadModal("contratos", files);
    e.target.value = ''; // Reset input
  };

  const handleUploadLegislacion = async (e) => {
    const files = Array.from(e.target.files);
    handleOpenUploadModal("legislacion", files);
    e.target.value = ''; // Reset input
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <TopNav user={user} onLogout={onLogout || (() => { localStorage.removeItem('token'); window.location.href='/'; })} />

      <main className="container mx-auto px-6 py-8">
        <Button 
          variant="outline" 
          onClick={() => navigate('/dashboard')} 
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Dashboard
        </Button>

        <div className="mb-8">
          <h2 className="text-2xl font-bold flex items-center gap-3" style={{ color: "var(--text)" }}>
            <Database className="h-6 w-6" style={{ color: "var(--primary)" }} />
            Data Lake
          </h2>
          <p className="text-tesla-grayMedium mt-2">
            Centro de almacenamiento de documentos legales y legislación para el sistema de IA
          </p>
          {user?.actuacion === "CEO" && (
            <a href="/correos-vault" className="inline-block mt-3 text-sm font-medium underline" style={{ color: "var(--primary)" }} data-testid="link-correos-vault">
              📧 Correos Vault (ingesta desde Google Drive) →
            </a>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Sección: Contratos y Pruebas */}
          <Card className="border-tesla-grayLight">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-xl text-tesla-black flex items-center gap-2">
                    <FileText className="h-5 w-5 text-tesla-red" />
                    Contratos y Pruebas
                  </CardTitle>
                  <CardDescription className="text-tesla-grayMedium mt-2">
                    Sube documentos legales como contratos, anexos y evidencias
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {puedeSubir ? (
                  <>
                    <div className="border-2 border-dashed border-tesla-grayLight rounded-lg p-8 text-center hover:border-tesla-red transition-colors">
                      <Upload className="h-12 w-12 text-tesla-grayMedium mx-auto mb-4" />
                      <Label 
                        htmlFor="upload-contratos" 
                        className="cursor-pointer inline-block"
                      >
                        <span className="text-tesla-black font-medium hover:text-tesla-red transition-colors">
                          {loadingContratos ? 'Subiendo...' : 'Haz clic para subir archivos'}
                        </span>
                        <p className="text-sm text-tesla-grayMedium mt-2">
                          PDF, DOCX (máx. 10MB por archivo)
                        </p>
                      </Label>
                      <Input
                        id="upload-contratos"
                        type="file"
                        multiple
                        accept=".pdf,.docx,.doc"
                        onChange={handleUploadContratos}
                        disabled={loadingContratos}
                        className="hidden"
                      />
                    </div>

                    <div className="bg-tesla-grayLighter rounded-lg p-4">
                      <h4 className="font-semibold text-tesla-black mb-2">Tipos de documentos:</h4>
                      <ul className="text-sm text-tesla-grayMedium space-y-1">
                        <li>• Contratos comerciales</li>
                        <li>• Documentos anexos</li>
                        <li>• Evidencias y pruebas</li>
                        <li>• Correspondencia legal</li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <div className="bg-tesla-grayLighter rounded-lg p-6 text-center">
                    <Database className="h-12 w-12 text-tesla-grayMedium mx-auto mb-3" />
                    <p className="text-sm text-tesla-grayMedium">
                      Solo puedes ver documentos. Contacta al CEO para obtener permisos de subida.
                    </p>
                  </div>
                )}

                {/* Lista de documentos subidos */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-tesla-black">
                      Documentos Subidos ({documentosContratos.length})
                    </h4>
                  </div>
                  
                  {loadingListaContratos ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tesla-red mx-auto"></div>
                    </div>
                  ) : documentosContratos.length === 0 ? (
                    <div className="text-center py-8 text-tesla-grayMedium text-sm">
                      No hay documentos subidos aún
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {documentosContratos.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-white border border-tesla-grayLight rounded-lg hover:border-tesla-red transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <FileText className="h-4 w-4 text-tesla-red flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-tesla-black truncate">
                                {doc.filename}
                              </p>
                              {doc.descripcion && (
                                <p className="text-xs text-tesla-grayMedium italic mt-1">
                                  {doc.descripcion}
                                </p>
                              )}
                              <p className="text-xs text-tesla-grayMedium mt-1">
                                {new Date(doc.fecha_subida).toLocaleDateString('es-CL', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadDataLake("contratos", doc)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`dl-descargar-contrato-${doc.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {puedeEliminar && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteContrato(doc.id, doc.filename)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sección: Legislación para IA */}
          <Card className="border-tesla-grayLight">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-xl text-tesla-black flex items-center gap-2">
                    <Book className="h-5 w-5 text-tesla-red" />
                    Documentos para IA
                  </CardTitle>
                  <CardDescription className="text-tesla-grayMedium mt-2">
                    Sube legislación chilena para el análisis de demandas
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {puedeSubir ? (
                  <>
                    <div className="border-2 border-dashed border-tesla-grayLight rounded-lg p-8 text-center hover:border-tesla-red transition-colors">
                      <Upload className="h-12 w-12 text-tesla-grayMedium mx-auto mb-4" />
                      <Label 
                        htmlFor="upload-legislacion" 
                        className="cursor-pointer inline-block"
                      >
                        <span className="text-tesla-black font-medium hover:text-tesla-red transition-colors">
                          {loadingLegislacion ? 'Subiendo...' : 'Haz clic para subir archivos'}
                        </span>
                        <p className="text-sm text-tesla-grayMedium mt-2">
                          PDF, DOCX (máx. 10MB por archivo)
                        </p>
                      </Label>
                      <Input
                        id="upload-legislacion"
                        type="file"
                        multiple
                        accept=".pdf,.docx,.doc"
                        onChange={handleUploadLegislacion}
                        disabled={loadingLegislacion}
                        className="hidden"
                      />
                    </div>

                    <div className="bg-tesla-grayLighter rounded-lg p-4">
                      <h4 className="font-semibold text-tesla-black mb-2">Tipos de documentos:</h4>
                      <ul className="text-sm text-tesla-grayMedium space-y-1">
                        <li>• Constitución de Chile</li>
                        <li>• Código Civil</li>
                        <li>• Código de Comercio</li>
                        <li>• Leyes y reglamentos relevantes</li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <div className="bg-tesla-grayLighter rounded-lg p-6 text-center">
                    <Database className="h-12 w-12 text-tesla-grayMedium mx-auto mb-3" />
                    <p className="text-sm text-tesla-grayMedium">
                      Solo puedes ver documentos. Contacta al CEO para obtener permisos de subida.
                    </p>
                  </div>
                )}

                {/* Lista de documentos subidos */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-tesla-black">
                      Documentos Subidos ({documentosLegislacion.length})
                    </h4>
                  </div>
                  
                  {loadingListaLegislacion ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tesla-red mx-auto"></div>
                    </div>
                  ) : documentosLegislacion.length === 0 ? (
                    <div className="text-center py-8 text-tesla-grayMedium text-sm">
                      No hay documentos subidos aún
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {documentosLegislacion.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-white border border-tesla-grayLight rounded-lg hover:border-tesla-red transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Book className="h-4 w-4 text-tesla-red flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-tesla-black truncate">
                                {doc.filename}
                              </p>
                              {doc.descripcion && (
                                <p className="text-xs text-tesla-grayMedium italic mt-1">
                                  {doc.descripcion}
                                </p>
                              )}
                              <p className="text-xs text-tesla-grayMedium mt-1">
                                {new Date(doc.fecha_subida).toLocaleDateString('es-CL', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadDataLake("legislacion", doc)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`dl-descargar-legislacion-${doc.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {puedeEliminar && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteLegislacion(doc.id, doc.filename)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Información adicional */}
        <Card className="mt-6 bg-tesla-grayLighter border-tesla-grayLight">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Database className="h-5 w-5 text-tesla-red mt-1" />
              <div>
                <h3 className="font-semibold text-tesla-black mb-2">Sobre el Data Lake</h3>
                <p className="text-sm text-tesla-grayMedium">
                  Los documentos subidos aquí se utilizan para entrenar y mejorar el sistema de IA. 
                  Los contratos y pruebas sirven como referencia para estructurar demandas similares, 
                  mientras que la legislación permite al sistema fundamentar legalmente las peticiones.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Modal de Descripción para Upload */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Descripción del Documento</DialogTitle>
            <DialogDescription>
              Proporciona una descripción para {uploadFiles.length > 1 ? `los ${uploadFiles.length} documentos` : 'el documento'} que vas a subir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {uploadFiles.length > 0 && (
              <div className="bg-tesla-grayLighter rounded-lg p-3">
                <p className="text-sm font-medium text-tesla-black mb-2">
                  {uploadFiles.length === 1 ? 'Archivo:' : 'Archivos:'}
                </p>
                {uploadFiles.map((file, idx) => (
                  <p key={idx} className="text-xs text-tesla-grayMedium">• {file.name}</p>
                ))}
              </div>
            )}
            <div>
              <Label htmlFor="descripcion">Descripción *</Label>
              <Textarea
                id="descripcion"
                placeholder="Ej: Código Civil de Chile actualizado 2024, Contrato marco Walmart proveedores, etc."
                value={uploadDescripcion}
                onChange={(e) => setUploadDescripcion(e.target.value)}
                rows={4}
                className="mt-2"
              />
              <p className="text-xs text-tesla-grayMedium mt-1">
                Esta descripción ayuda a identificar el contenido del documento.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadModal(false);
                setUploadFiles([]);
                setUploadDescripcion("");
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmUpload}
              disabled={!uploadDescripcion.trim()}
              className="bg-tesla-red hover:bg-red-700 text-white"
            >
              Subir {uploadFiles.length > 1 ? `${uploadFiles.length} Documentos` : 'Documento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-tesla-black border-t border-tesla-grayLight mt-12">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <img src="/allcom-logo.png" alt="Allcom" className="h-8" />
              <div className="text-sm text-tesla-grayMedium">
                <p>© 2025 Allcom Technologies</p>
              </div>
            </div>
            <div className="text-sm text-tesla-grayMedium">
              <p>Data Lake - Sistema de gestión documental</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default DataLake;
